// The pure half of single-setting.component.vue: reading a declared range out of a setting's
// metadata, and reading a stored INT value the way the server reads it. Extracted so it can be
// tested without mounting the component — these are the rules that have to agree with
// `declared_setting_range` and `validate_setting_value` in briteapps-admin's core/utils.py, and
// the two ends drifting apart is exactly the class of bug this module exists to prevent.

// What Python's int() accepts on a stripped string: an optional sign, decimal digits, and single
// underscores between digit groups (int('1_000') is 1000, int('_1') and int('1__0') are errors).
// int() also takes non-ASCII digits, which \d does not match here — those are rare enough that
// treating them as unshowable is the safer direction, since it warns instead of inventing a number.
export const WHOLE_NUMBER_SYNTAX = /^[+-]?\d+(_\d+)*$/

// beyond this, JavaScript cannot hold the value exactly and would show a different number
export const LARGEST_EXACT_INTEGER = 9007199254740991

export function isUsableBound (value) {
  // Mirrors declared_setting_range on the server: a bound that is not a finite number is not a
  // bound, because it cannot be compared against. The two ends have to agree — otherwise the field
  // enforces a limit the server does not have, and a null reads out to the user as the word "null".
  // typeof guards the coercion global isFinite would otherwise do, and the pair rules out NaN,
  // ±Infinity and booleans.
  return typeof value === 'number' && isFinite(value)
}

export function declaredRange (metadata) {
  // ±Infinity where nothing usable is declared, so callers can compare without special cases.
  let result = {min: -Infinity, max: Infinity}
  let range = metadata && metadata.validation && metadata.validation.range
  if (!range) {
    return result
  }
  if (isUsableBound(range.min)) {
    result.min = range.min
  }
  if (isUsableBound(range.max)) {
    result.max = range.max
  }
  return result
}

export function asWholeNumber (value) {
  // Number() is far more generous than int(): it reads "1e3" as 1000, "0x10" as 16 and "1." as 1,
  // all of which int() rejects, and it silently rounds 9007199254740993 down to ...992. So the
  // string is matched against int()'s own syntax before being converted, and anything JavaScript
  // cannot hold exactly is refused rather than shown as a different number.
  if (value === null || value === undefined) {
    return null
  }
  let text = String(value).trim()
  if (!WHOLE_NUMBER_SYNTAX.test(text)) {
    return null
  }
  let parsed = Number(text.replace(/_/g, ''))
  if (Math.abs(parsed) > LARGEST_EXACT_INTEGER) {
    return null
  }
  return parsed
}

export function allowedRangeText (declared) {
  if (declared.min !== -Infinity && declared.max !== Infinity) {
    return declared.min + ' to ' + declared.max
  }
  if (declared.min !== -Infinity) {
    return declared.min + ' or higher'
  }
  if (declared.max !== Infinity) {
    return declared.max + ' or lower'
  }
  return 'unrestricted'
}

export function shownRange (declared, storedNumber) {
  // What the control may show. el-input-number clamps whatever it is given to :min/:max, so a
  // stored value outside the declared range would render as the bound rather than as itself — the
  // field would disagree with the database. Widen just far enough to admit the real value; once a
  // valid one is saved the declared bounds apply again.
  if (storedNumber === null) {
    return declared
  }
  return {
    min: Math.min(declared.min, storedNumber),
    max: Math.max(declared.max, storedNumber),
  }
}

export function withinDeclaredRange (declared, value) {
  // declared is ±Infinity where nothing is declared, so this is a no-op there
  return Math.min(Math.max(value, declared.min), declared.max)
}

export function valueOutOfRange (declared, storedNumber) {
  if (storedNumber === null) {
    return false
  }
  return storedNumber < declared.min || storedNumber > declared.max
}
